export interface Project {
  id: string;
  title: string;
  subtitle: string;
  description: string[];
  tech: string;
  link?: string;
  accent: string;
  version?: string;
}

// Studio/Brand Website Projects
export interface StudioProject {
  id: string;
  title: string;
  tagline: string;
  description: string;
  liveUrl: string;
  techStack: string[];
  category: 'e-commerce' | 'saas' | 'showcase' | 'subscription';
  accentColor: string;
  features: string[];
  image: string;
}

export const STUDIO_PROJECTS: StudioProject[] = [
  {
    id: "gravity",
    title: "GRAVITY",
    tagline: "Cloud-Based Artisan Ordering Platform",
    description: "Complete ordering platform for artisan food brands combining brutalist-luxe design with enterprise-level operational features. Includes a comprehensive Admin Panel and real-time Kitchen Dashboard for seamless order management.",
    liveUrl: "https://saadmughal-gravity.vercel.app/",
    techStack: ["React 19", "TypeScript", "GSAP", "Supabase", "Tailwind CSS"],
    category: "e-commerce",
    accentColor: "#DEB887",
    features: ["Real-time order tracking", "Live customer-to-kitchen chat", "Thermal receipt generation", "Admin Panel & Kitchen Dashboard"],
    image: "/assets/projects/gravity.jpg"
  },
  {
    id: "perfect-motors",
    title: "PERFECT MOTORS",
    tagline: "Premium Car Dealership Redesign",
    description: "Client-commissioned redesign for Dubai-based dealership. Clean card-based vehicle listings with promotional badges, discount strikethroughs, and Laravel backend integration ready.",
    liveUrl: "https://perfect-motors.vercel.app/",
    techStack: ["React", "Tailwind CSS", "Inertia.js", "Framer Motion", "GSAP"],
    category: "e-commerce",
    accentColor: "#DC2626",
    features: ["Card-based vehicle listings", "Promotional badge system", "Price transparency", "Laravel integration ready"],
    image: "/assets/projects/perfect-motors.png"
  },
  {
    id: "lumina",
    title: "LUMINA",
    tagline: "Advanced Scrollytelling Platform",
    description: "Featuring a custom-built canvas engine and performance-optimized motion logic. Includes adaptive viewport scaling, elegant onboarding sequence, and sophisticated background buffering for a seamless user journey.",
    liveUrl: "https://saadmughal-lumina.vercel.app/",
    techStack: ["Next.js", "GSAP", "Canvas Sequence Rendering"],
    category: "showcase",
    accentColor: "#00F5FF",
    features: ["Custom canvas engine", "Adaptive viewport scaling", "Hold-to-enter onboarding", "Background buffering"],
    image: "/assets/projects/lumina.jpg"
  },
  {
    id: "threada",
    title: "THREADA",
    tagline: "High-Performance Fashion Commerce",
    description: "High-performance fashion e-commerce platform engineered for speed and visual immersion. Features a robust Admin Panel and specialized Fulfillment/Orders Dashboard with real-time Kanban tracking.",
    liveUrl: "https://saadmughal-threada.vercel.app/",
    techStack: ["React (Vite)", "TypeScript", "Supabase", "Zustand", "Three.js"],
    category: "e-commerce",
    accentColor: "#ffffff",
    features: ["3D Interactive Hero", "Real-time Kanban Dashboard", "Admin Panel & Orders Dashboard", "Splash Cursor Premium"],
    image: "/assets/projects/threada.jpg"
  },
  {
    id: "gusto",
    title: "GUSTO",
    tagline: "Fire-Forged Digital Flagship",
    description: "High-fidelity digital experience for artisan pizza featuring the 'Studio Rotator' cinematic gallery and kinetic typography. Discord integration for instant kitchen ticket streaming.",
    liveUrl: "https://saadmughal-gusto.vercel.app/",
    techStack: ["React 19", "TypeScript", "GSAP", "Lenis", "PWA"],
    category: "e-commerce",
    accentColor: "#2E4A47",
    features: ["Studio Rotator gallery", "Discord kitchen integration", "Thermal receipt system", "PWA architecture"],
    image: "/assets/projects/gusto.jpg"
  },
  {
    id: "vantage",
    title: "VANTAGE",
    tagline: "Life OS Landing Experience",
    description: "Synchronized digital sanctuary with 3D-perspective hero elements, Feature Switcher, and fan-out testimonials. Electric gold accents on sophisticated dark theme.",
    liveUrl: "https://saadmughal-vantage.vercel.app/",
    techStack: ["React", "Tailwind CSS", "Framer Motion"],
    category: "saas",
    accentColor: "#D4AF37",
    features: ["3D perspective hero", "Feature Switcher component", "Fan-out testimonials", "Scroll-linked progressions"],
    image: "/assets/projects/vantage.jpg"
  },
  {
    id: "wakuwaku",
    title: "WAKUWAKU",
    tagline: "Premium Japanese Subscription Platform",
    description: "Premium Kawaii design language for East Asian luxury market. Bilingual EN/JP experience with glassmorphism overlays and elegant serif typography.",
    liveUrl: "https://saadmughal-wakuwaku.vercel.app/",
    techStack: ["React 19", "Tailwind CSS", "Glassmorphism"],
    category: "subscription",
    accentColor: "#D4A5A5",
    features: ["Bilingual EN/JP toggle", "Modal subscription system", "Ovoid mascot branding", "Interactive curation galleries"],
    image: "/assets/projects/wakuwaku.jpg"
  },
  {
    id: "novus",
    title: "NOVUS",
    tagline: "Renewable Energy Showcase",
    description: "High-performance showcase with stretchy reveal animations, parallax hero, and Apple-esque UX for sustainable infrastructure. Carbon tones with vibrant signature green.",
    liveUrl: "https://saadmughal-novus.vercel.app/",
    techStack: ["React 19", "Tailwind CSS", "Intersection Observer"],
    category: "showcase",
    accentColor: "#22C55E",
    features: ["Parallax hero section", "Auto-playing case carousel", "Scroll-triggered storytelling", "Stretchy reveal animations"],
    image: "/assets/projects/novus.png"
  }
];

// Agentic Systems Projects (formerly PROJECTS)
export const AGENTIC_PROJECTS: Project[] = [
  {
    id: "project-outreach",
    title: "Project Outreach",
    subtitle: "Agency OS // B2B Command Center",
    version: "v3.0.1",
    description: [
      "Automates lead discovery and hyper-personalized messaging to solve manual prospecting inefficiencies.",
      "Utilizes Gemini 3 Pro with Google Search tool for real-time grounding and synthetic script generation.",
      "Production-ready tactical dashboard with automated batch delivery via SMTP2GO.",
      "Persistent data synchronization through Supabase for high-conversion scalable pipelines."
    ],
    tech: "Gemini 3 Pro, Google Search Tool, Next.js, Supabase, SMTP2GO",
    link: "L3BwYS5sZWNyZXYuMnNjLTRzdGNlcHNvcnAtMXJvdGFyZXBvLy86c3B0dGg=",
    accent: "#a3ff00"
  },
  {
    id: "operator-cs",
    title: "Operator - Control Systems",
    subtitle: "Industrial Digital Storefront",
    version: "v1.0.0",
    description: [
      "Elite technical showcase for high-performance industrial control systems (SCADA).",
      "Prioritizes millisecond-level precision, Zero Trust security, and high-fidelity visualization.",
      "Architected for integration with Gemini 3 Pro for predictive maintenance logic.",
      "Serves as my company's 'billion-dollar face' to demonstrate deployment-ready capabilities."
    ],
    tech: "React, SCADA Visualization, Zero Trust Security, Gemini 3 Pro Ready",
    link: "L3BwYS5sZWNyZXYuc2Mtcm90YXJlcG8vLzpzcHR0aA==",
    accent: "#bf00ff"
  },
  {
    id: "pharma-vision",
    title: "PHARMA-VISION",
    subtitle: "Clean Room Digital Twin HMI",
    version: "v2.2.0",
    description: [
      "Monitors sterile filling lines to prevent batch contamination and equipment failure.",
      "Utilizes Gemini 3 Flash to analyze complex telemetry and provide predictive maintenance insights.",
      "Physics-based conveyor animations and integrated environmental gauges for GMP-grade support.",
      "Automated defect detection system to eliminate manual inspection errors."
    ],
    tech: "Gemini 3 Flash, React, Physics Engine, Real-time Telemetry",
    link: "L3BwYS5sZWNyZXYubm9pc2l2LWFtcmFocC8vOnNwdHRo",
    accent: "#00f5ff"
  },
  {
    id: "project-dispatch",
    title: "Project DISPATCH",
    subtitle: "Automated Logistics Dashboard",
    version: "v2.5.0",
    description: [
      "Optimizes HVAC technician routing to solve real-world fleet inefficiency and communication delays.",
      "Utilizes Gemini 2.5 Flash to generate professional radio-style dispatch messages.",
      "Interactive vector/satellite mapping with real-time telemetry analysis.",
      "Live performance charting with Recharts and persistent fleet management."
    ],
    tech: "Gemini 2.5 Flash, React, Recharts, LocalStorage, Maps API",
    link: "L3BwYS5sZWNyZXYuaXNwLWhjdGFwc2lkLXRjZWpvcnAvLzpzcHR0aA==",
    accent: "#a3ff00"
  },
  {
    id: "sync-ops",
    title: "Project SYNC // OPS",
    subtitle: "Intelligent Automation Hub",
    version: "v2.1.0",
    description: [
      "Bridges Shopify, Airtable, and Slack to solve manual order monitoring bottlenecks.",
      "Transforms raw data into insights like VIP alerts or reorder protocols using Gemini 2.5 Flash.",
      "High-fidelity topology visualizer with real-time metrics and configuration suite.",
      "Utilizes Shopify Webhooks and Airtable API for intelligent record-keeping."
    ],
    tech: "Gemini 2.5 Flash, Shopify Webhooks, Airtable API, Slack Integration",
    link: "L3BwYS5sZWNyZXYuc3BvLWNueXMtdGNlam9ycC8vOnNwdHRo",
    accent: "#00f5ff"
  },
  {
    id: "pulse-lake",
    title: "Project PULSE",
    subtitle: "Autonomous Infrastructure Monitor",
    version: "v1.8.4",
    description: [
      "Autonomous health monitoring tracking CPU, memory, and latency across server nodes.",
      "Root cause analysis performed by Gemini 2.5 Flash on error logs during service crashes.",
      "Triggers automated 'restart protocol' state machine for zero-touch restoration.",
      "Instant detection of service interruptions with intelligent response."
    ],
    tech: "Gemini 2.5 Flash, Node Telemetry, State Machines, Root Cause Analysis",
    link: "L3BwYS5sZWNyZXYuZWthbC1lc2x1cC10Y2Vqb3JwLy86c3B0dGg=",
    accent: "#ff0044"
  },
  {
    id: "amplify",
    title: "Project AMPLIFY",
    subtitle: "AI Distribution Engine",
    version: "v2.0.0",
    description: [
      "Automates short-form video repurposing for TikTok, Reels, and YouTube Shorts.",
      "Gemini 2.5 Flash high-speed visual analysis with JSON Response Schema enforcement.",
      "Enterprise-ready white-label dashboard with campaign history tracking.",
      "Optimizes viral-ready content Repurposing with Structured Metadata."
    ],
    tech: "Gemini 2.5 Flash, JSON Schema, Browser APIs, Tailwind CSS",
    link: "L3BwYS5sZWNyZXYuZWdpZWIteWZpbHBtYS10Y2Vqb3JwLy86c3B0dGg=",
    accent: "#bf00ff"
  },
  {
    id: "booking-zeta",
    title: "Project BOOKING",
    subtitle: "Practice Management AI",
    version: "v1.2.0",
    description: [
      "Matches high-priority waitlisted patients to vacated slots using Gemini 2.5 Flash.",
      "Drafts personalized SMS/WhatsApp confirmations to eliminate revenue loss.",
      "Dynamic white-labeling theme studio and persistent data engine.",
      "Solves the challenge of empty clinical chairs via intelligent patient matching."
    ],
    tech: "Gemini 2.5 Flash, Structured JSON, LocalStorage, React",
    link: "L3BwYS5sZWNyZXYuYXRlei1nbmlrb29iLXRjZWpvcnAvLzpzcHR0aA==",
    accent: "#a3ff00"
  },
  {
    id: "scout-os",
    title: "Project SCOUT",
    subtitle: "Automated Recruitment OS",
    version: "v2.4.0",
    description: [
      "Parses PDF resumes and scores candidates against job criteria using AI.",
      "Structured JSON extraction of precise skills and experience via Gemini 2.5 Flash.",
      "One-click Google Calendar scheduling and pre-filled email automation.",
      "Solves the manual bottleneck of high-volume HR screening."
    ],
    tech: "Gemini 2.5 Flash, PDF Parsing, Google Calendar API, React",
    link: "L3BwYS5sZWNyZXYudHVvY3MtdGNlam9ycC8vOnNwdHRo",
    accent: "#00f5ff"
  },
  {
    id: "lumina-ai",
    title: "Lumina Support AI",
    subtitle: "Autonomous E-commerce Concierge",
    version: "v3.1.0",
    description: [
      "Automates FAQ resolution, order tracking, and complex return inquiries.",
      "Gemini 2.5 Flash function calling for real-time interaction with business logic.",
      "Dual-view dashboard with a 'Setup Wizard' for instant brand deployment.",
      "Elegant customer interface paired with detailed system monitoring."
    ],
    tech: "Gemini 2.5 Flash, Function Calling, Setup Wizard, Mock-DB",
    link: "L3BwYS5sZWNyZXYuaWEtdHJvcHB1cy1hbmltdWwvLzpzcHR0aA==",
    accent: "#bf00ff"
  },
  {
    id: "stockpile",
    title: "Project STOCKPILE",
    subtitle: "High-Fidelity Inventory Dashboard",
    version: "v2.5.0",
    description: [
      "Synchronizes stock levels across Shopify, Amazon, and physical POS in real-time.",
      "Proactive supply chain risk analysis powered by Gemini 2.5 Flash.",
      "Developer-centric JSON webhook injector for testing realistic sync engines.",
      "Prevents overselling by mimicking live API traffic across connected channels."
    ],
    tech: "Gemini 2.5 Flash, Webhooks, Shopify/Amazon Simulation, LocalStorage",
    link: "L3BwYS5sZWNyZXYuZWRhai1lbGlwa2NvdHMtdGNlam9ycC8vOnNwdHRo",
    accent: "#a3ff00"
  },
  {
    id: "extract-ai",
    title: "Project EXTRACT",
    subtitle: "Automated Financial Pipeline",
    version: "v2.0.0",
    description: [
      "Instantly parses PDF and image invoices using multimodal Gemini 2.5 Flash.",
      "Structured JSON Schema enforcement for categorization and data extraction.",
      "Interactive AI document auditing (chat) and webhook-ready ERP synchronization.",
      "Transforms raw financial files into actionable accounting data."
    ],
    tech: "Gemini 2.5 Flash, Multimodal OCR, JSON Schema, React",
    link: "L3BwYS5sZWNyZXYudGNhcnR4ZS10Y2Vqb3JwLy86c3B0dGg=",
    accent: "#00f5ff"
  },
  {
    id: "nexus-logic",
    title: "NEXUS",
    subtitle: "Digital Logic Architect",
    version: "v2.5.0",
    description: [
      "Simplifies design and simulation of complex circuits from natural language prompts.",
      "Zen ADHD focus mode with automated Truth Table analysis.",
      "Gemini 2.5 Flash structured JSON generation for circuit architecture.",
      "Crash-proof workflow with Auto-Save and 3-session recovery history."
    ],
    tech: "Gemini 2.5 Flash, Logic Simulation, Truth Table Analysis, React",
    link: "L3BwYS5sZWNyZXYudGNldGloY3JhLWNpZ29sLXN1eGVuLy86c3B0dGg=",
    accent: "#a3ff00"
  }
];

export const EXPERIENCE = [
  {
    title: "Founder & Technical Lead",
    company: "Operator Control Systems",
    period: "2025 – Present",
    bullets: [
      "Founded an industrial control systems engineering firm focusing on high-fidelity AI platforms and modern HMI/SCADA solutions.",
      "Spearheaded multi-industry deployment of Zero Trust industrial architectures.",
      "Leading R&D for predictive maintenance logic using Gemini 3 Pro and real-time telemetry."
    ]
  },
  {
    title: "Founder & Lead Engineer",
    company: "Batch Systems",
    period: "2025 – Present",
    bullets: [
      "Established the web-development branch of Operator Control Systems, focusing on high-end, award-winning design aesthetics.",
      "Architecting premium web experiences characterized by minimalist layouts, maximalist typography, and complex 3D integrations.",
      "Building custom data-driven dashboard UIs and full-stack web applications for global clients."
    ]
  },
  {
    title: "E-Commerce Entrepreneur",
    company: "Watch Mafia & Multiple Brands",
    period: "2021 – 2024",
    bullets: [
      "Founded and scaled multiple profitable brands, including Watch Mafia, leveraging advanced marketing automation.",
      "Engineered custom e-commerce websites and robust ordering systems (OFOS, RMS, POS).",
      "Developed internal AI tools to manage inventory and client acquisition at scale, leading to 5 successful brand exits."
    ]
  }
];

export const SKILLS = {
  "AI / ML & Agentic": "Gemini 2.5/3 Pro, LLM Orchestration, Prompt Engineering, Function Calling, JSON Schema, Agentic Workflows",
  "Full-Stack": "React / Next.js, TypeScript, Node.js, Python, SQL, Supabase, PostgreSQL",
  "Creative Frontend": "Tailwind CSS, GSAP, Framer Motion, Canvas API, Three.js",
  "Industrial Tech": "HMI/SCADA, OPC UA, Industrial IoT, Real-time Telemetry, Digital Twins",
  "DevOps & Tools": "Docker, Webhooks, REST APIs, Git/GitHub, Vercel"
};

// Backward compatibility alias
export const PROJECTS = AGENTIC_PROJECTS;
